import { inject, Injectable, signal } from "@angular/core";
import { distinctUntilChanged, Observable, filter, combineLatest } from "rxjs";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import {
  CountryFilter,
  CountryMainFilterType,
  MainFilter,
} from "@bitwarden/web-vault/app/models/types/filter.types";
import { FilterControlType } from "@bitwarden/web-vault/app/services/store/calculation-base/controls/filter.control";
import { ViewsFilterInstitutionHelper } from "@bitwarden/web-vault/app/gloss/views/views-control/views-customize/views.filter.helper.institution";
import { DataPluginStoreService } from "@bitwarden/web-vault/app/services/store/data-plugin/data-plugin.store.service";
import { ScenarioStoreService } from "../../../../services/store/scenario/scenario.store.service";

@Injectable({
  providedIn: "root",
})
export class ScenariosFilter {
  private scenarioStore = inject(ScenarioStoreService);
  private dataPluginStore = inject(DataPluginStoreService);
  private institutionFilterHelper = inject(ViewsFilterInstitutionHelper);

  private filtersSelected$ = toObservable(this.scenarioStore.filters.selected);
  private filtersOptions$ = toObservable(this.scenarioStore.filters.options);

  options = this.scenarioStore.filters.options();
  selected: FilterControlType = { ...this.scenarioStore.filters.selected() };

  filter = signal<MainFilter<CountryMainFilterType>>(null);
  filterObservable$: Observable<MainFilter<CountryMainFilterType>> = toObservable(this.filter);

  constructor() {
    const filterSelectedChange$ = this.filtersSelected$.pipe(
      filter((x) => !!x),
      distinctUntilChanged((x, y) => JSON.stringify(x) === JSON.stringify(y)),
      takeUntilDestroyed(),
    );

    const filterOptionsChange$ = this.filtersOptions$.pipe(
      filter((x) => !!x),
      distinctUntilChanged((x, y) => JSON.stringify(x) === JSON.stringify(y)),
      takeUntilDestroyed(),
    );

    combineLatest([filterSelectedChange$, filterOptionsChange$]).subscribe(
      this.onFiltersChanged.bind(this),
    );
  }

  resetFilter() {
    this.selected.countries = this.options.countries;
    this.selected.institutions = this.options.institutions;
    this.selected.accounts = this.options.accounts;
    this.setFilter();
  }

  setFilter() {
    const countryMap: Map<string, CountryFilter> = new Map();
    for (const countryCode of this.options.countries) {
      const countryViewList = this.dataPluginStore.theCountryList.theCountryViews();
      const countryView = countryViewList.find((cv) => cv.code === countryCode);
      const isSelectedCountry = this.selected.countries.includes(countryCode);
      const countryFilter: CountryFilter = {
        view: countryView,
        childFilter: this.institutionFilterHelper.getFilter(
          this.options,
          this.selected,
          countryView,
        ),
        isDisabled: false,
        isSelected: isSelectedCountry,
      };
      countryMap.set(countryCode, countryFilter);
    }

    const filter: MainFilter<CountryMainFilterType> = {
      countryFilter: countryMap,
    };

    this.filter.set(filter);
  }

  onFiltersChanged() {
    this.options = this.scenarioStore.filters.options();
    this.selected = { ...this.scenarioStore.filters.selected() };
    this.setFilter();
  }

  onCountrySelect(selectedCountryCodes: string[]) {
    this.selected.countries = selectedCountryCodes;
    this.setFilter();
  }

  onInstitutionSelect(selectedInstitutionIds: string[]) {
    this.selected.institutions = selectedInstitutionIds;
    this.setFilter();
  }

  onAccountSelect(selectedAccountIds: string[]) {
    this.selected.accounts = selectedAccountIds;
    this.setFilter();
  }
}
